import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { fetchProjectUpdates } from '@/features/milestones/api/projectUpdatesApi'

function UpdateCard({ update }) {
  const formatDate = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now - date
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffDays === 0) return 'Today'
    if (diffDays === 1) return 'Yesterday'
    if (diffDays < 7) return `${diffDays} days ago`
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`
    return date.toLocaleDateString('en-NZ', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  const getUpdateIcon = (type) => {
    switch (type) {
      case 'milestone':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
      case 'photos':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        )
      case 'progress':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        )
      default:
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )
    }
  }

  return (
    <Link
      to={`/projects/${update.project_id}`}
      className="block bg-white border border-neutral-200 rounded-xl p-4 hover:border-primary-300 hover:shadow-md transition-all group"
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-10 h-10 bg-primary-50 text-primary-600 rounded-full flex items-center justify-center group-hover:bg-primary-100 transition-colors">
          {getUpdateIcon(update.type)}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <h4 className="font-semibold text-neutral-900 group-hover:text-primary-600 transition-colors line-clamp-1">
              {update.project?.name || 'Project Update'}
            </h4>
            <span className="text-xs text-neutral-500 whitespace-nowrap">
              {formatDate(update.created_at)}
            </span>
          </div>

          <p className="text-sm text-neutral-600 mb-2 line-clamp-2">
            {update.message}
          </p>

          {update.images?.length > 0 && (
            <div className="flex gap-2 mt-3">
              {update.images.slice(0, 3).map((img, idx) => (
                <div
                  key={idx}
                  className="w-16 h-16 rounded-lg overflow-hidden bg-neutral-100"
                >
                  <img
                    src={img}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
              {update.images.length > 3 && (
                <div className="w-16 h-16 rounded-lg bg-neutral-100 flex items-center justify-center text-xs font-medium text-neutral-600">
                  +{update.images.length - 3}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Link>
  )
}

export function ProjectUpdatesTimeline({ projectIds = [] }) {
  const [filter, setFilter] = useState('all')

  const { data: updates = [], isLoading } = useQuery({
    queryKey: ['project-updates-timeline', projectIds],
    queryFn: async () => {
      if (!projectIds.length) return []
      const allUpdates = []
      for (const projectId of projectIds) {
        const projectUpdates = await fetchProjectUpdates(projectId)
        allUpdates.push(...projectUpdates)
      }
      return allUpdates.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    },
    enabled: projectIds.length > 0,
  })

  const filteredUpdates = filter === 'all'
    ? updates
    : updates.filter(u => u.type === filter)

  const updateTypes = [
    { key: 'all', label: 'All Updates' },
    { key: 'milestone', label: 'Milestones' },
    { key: 'progress', label: 'Progress' },
    { key: 'photos', label: 'Photos' },
  ]

  return (
    <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden">
      <div className="p-6 pb-4 border-b border-neutral-100">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-neutral-900">
            Latest Updates
          </h3>
          <Link
            to="/projects"
            className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
          >
            View all
          </Link>
        </div>

        <div className="flex gap-2 overflow-x-auto pb-2 -mb-2 no-scrollbar">
          {updateTypes.map(type => (
            <button
              key={type.key}
              onClick={() => setFilter(type.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                filter === type.key
                  ? 'bg-primary-600 text-white shadow-sm'
                  : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
              }`}
            >
              {type.label}
            </button>
          ))}
        </div>
      </div>

      <div className="p-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="spinner spinner-md text-primary-600"></div>
          </div>
        ) : filteredUpdates.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 bg-neutral-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-neutral-600">
              No updates yet. Check back soon for project progress.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredUpdates.slice(0, 5).map(update => (
              <UpdateCard key={update.id} update={update} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
